import type {
  InfluencerPaymentHistorySerializer,
  InfluencerProfileDetailSerializer,
  UGCMediaCompletedSerializer,
} from "@/generated/DjangoTypes";
import {useSliceInfluencerQuery} from "@/hooks/useInfluencer";
import {useGenericInfiniteQuery, useGenericInfiniteQueryWithCount, useGenericQuery} from "@/hooks/useQueryHooks";
import {useSelectedCompanyId} from "@/hooks/useUserHooks";
import {extractCutoffFromPageParams, sumCountsByCutoffGroup} from "@/hooks/useVibeSearchBroadening";
import {client} from "@/httpClient";
import {i18n} from "@/i18n";
import {queryClient} from "@/queryClient";
import {FILTER_UNSAVEABLE_KEYS, type TFilters} from "@/store/filterStoreUtils";
import type {
  TCampaignType,
  TInfiniteQueryOptions,
  TInfluencerContent,
  TPaginated,
  TQueryOptions,
  TQueryResultWithCount,
  TVibePaginated,
  TVibeQueryResult,
} from "@/store/types";
import {
  type UseInfiniteQueryResult,
  type UseMutationOptions,
  useInfiniteQuery,
  useMutation,
} from "@tanstack/react-query";
import type {AxiosError, AxiosResponse} from "axios";
import {uniqBy} from "lodash";
import omit from "lodash/omit";

export const INFLUENCER_KEYS = {
  mCreateGroupFromFilters: ["/influencer/create_group_from_filters/"],
  mDeleteInfluencerFilter: (filterId: number) => [`/influencer_filters/${filterId}/`],
  mDeleteVibeSearchHistory: (id: number) => [`/influencer/vibe_search_history/?id=${id}`],
  mInfluencerFilter: ["/influencer_filters/"],
  mInfluencerInstagramFeed: (influencerId: number, mediaId: string) => [
    `/influencer/${influencerId}/instagram_feed/${mediaId}/`,
  ],
  mUgcVideoVerification: ["/influencer/ugc_verification/"],
  mUnsubscribeAllNotifications: (influencerId: number) => [
    `/influencer/${influencerId}/unsubscribe_all_notifications/`,
  ],
  mUpdateInfluencerFilter: (filterId: number) => [`/influencer_filters/${filterId}/`],
  qClothingSizeOptions: () => ["/influencer/clothing_size_options/"],
  qInfluencer: (influencerId: number, params?: string) => [`/influencer/${influencerId}/?${params}`],
  qInfluencerFilters: ({
    agencyId,
    companyId,
    search,
    type,
  }: {
    agencyId?: number;
    companyId?: number;
    search?: string;
    type?: TCampaignType;
  }) => [
    `/influencer_filters/?${agencyId ? `agency_id=${agencyId}` : companyId ? `company_id=${companyId}` : ""}${type ? `&type=${type}` : ""}${search ? `&search=${search}` : ""}`,
  ],
  qInfluencerActiveCampaigns: (influencerId: number) => [`/influencer/${influencerId}/active_campaigns/`],
  qInfluencerAwards: ({excludeVerified = true, influencerId}: {excludeVerified?: boolean; influencerId: number}) => [
    `/influencer/${influencerId}/awards/${excludeVerified ? "?exclude_verified=true" : ""}`,
  ],
  qInfluencerCampaignsWorkedWith: (influencerId: number, params?: string) => [
    `/influencer/${influencerId}/campaigns_worked_with/?${params}`,
  ],
  qInfluencerCompletedContent: (influencerId: number, params = "") => [
    `/influencer/${influencerId}/completed_content/${params ? `?${params}` : ""}`,
  ],
  qInfluencerCompletedUgcApplications: (influencerId: number) => [
    `/influencer/${influencerId}/completed_ugc_applications/`,
  ],
  qInfluencerCount: (params?: string | null) => [`/influencer/?${params}&count_only=true`],
  qInfluencerPlatformCounts: (params?: string | null) => [`/influencer/platform_counts/?${params}`],
  qInfluencerInstagramFeed: (params?: string) => [`/influencer/instagram_feed/?${params}`],
  qInfluencerList: (params?: string | null) => [`/influencer/?${params}`],
  qInfluencerNetworkData: (params?: string) => [`/influencer/network_data/?${params}`],
  qInfluencerPaymentHistory: (influencerId: number) => [`/influencer/${influencerId}/payment_history/`],
  qInfluencerPendingCampaigns: (influencerId: number) => [`/influencer/${influencerId}/pending_campaigns/`],
  qInfluencerTikTokFeed: (params?: string) => [`/influencer/tiktok_feed/?${params}`],
  qVibeSearchHistory: (search?: string) => [`/influencer/vibe_search_history/${search ? `?search=${search}` : ""}`],
  qSimilarInfluencers: (influencerId: number, params?: string) => [
    `/influencer/${influencerId}/similar_influencers/?${params}`,
  ],
  qSimilarInfluencersNewGroup: (influencerId: number, params?: string) => [
    `/influencer/${influencerId}/similar_influencers_new_group/?${params}`,
  ],
} as const;

export const useInfluencerQuery = (params: {
  id: number;
  sourceParams?: string;
  queryOptions?: TQueryOptions<InfluencerProfileDetailSerializer>;
}) => {
  const {id, sourceParams, queryOptions} = params;
  const companyId = useSelectedCompanyId();

  return useGenericQuery<InfluencerProfileDetailSerializer>({
    queryKey: INFLUENCER_KEYS.qInfluencer(
      id,
      `${sourceParams ?? ""}${!!sourceParams?.length ? "&" : ""}${!!companyId ? "company_id=" + companyId : ""}`,
    ),
    enabled: !!id,
    ...queryOptions,
  });
};

export type TClothingSizeOptions = {below: string[]; shoe_size: string[]; top: string[]};

export const useClothingSizeOptionsQuery = (queryOptions?: TQueryOptions<TClothingSizeOptions>) =>
  useGenericQuery<TClothingSizeOptions>({
    queryKey: INFLUENCER_KEYS.qClothingSizeOptions(),
    staleTime: Infinity,
    ...queryOptions,
  });

export const stripParamKeys = (params: string, keys = ["cutoff_value", "maximum_cutoff_value"]): string => {
  const urlParams = new URLSearchParams(params);
  keys.forEach(key => urlParams.delete(key));
  return urlParams.toString();
};

export const useInfluencerListQuery = ({
  queryOptions,
  searchParams,
}: {
  queryOptions?: TInfiniteQueryOptions<
    InfluencerProfileDetailSerializer,
    AxiosError,
    TVibeQueryResult<InfluencerProfileDetailSerializer>
  >;
  searchParams?: string | null;
}) => {
  const {sliceInfluencers} = useSliceInfluencerQuery();
  const stableParams = searchParams ? stripParamKeys(searchParams) : searchParams;

  return useGenericInfiniteQueryWithCount({
    queryKey: INFLUENCER_KEYS.qInfluencerList(stableParams),
    initialPageParam: stableParams ? INFLUENCER_KEYS.qInfluencerList(stableParams)[0] : undefined,
    select: response => {
      const firstPage = response.pages[0]?.data as TVibePaginated<InfluencerProfileDetailSerializer>;

      return {
        count: sumCountsByCutoffGroup(response.pageParams, response.pages),
        cutoffValue: extractCutoffFromPageParams(response.pageParams),
        frequencyDistribution: firstPage?.frequency_distribution ?? {},
        noCutoff: !!firstPage?.no_cutoff,
        results: sliceInfluencers({
          hasNext: !!response.pages[response.pages.length - 1]?.data.next,
          influencers: uniqBy(
            response.pages.flatMap(page => page.data.results),
            "uuid",
          ),
        }),
      };
    },
    ...queryOptions,
  }) as UseInfiniteQueryResult<TVibeQueryResult<InfluencerProfileDetailSerializer>>;
};

export const useInfluencerContentQuery = ({
  influencerId,
  campaignType,
}: {
  influencerId: number;
  campaignType?: TCampaignType;
}) => {
  const queryKey =
    campaignType === "ugc"
      ? INFLUENCER_KEYS.qInfluencerCompletedUgcApplications(influencerId)
      : INFLUENCER_KEYS.qInfluencerCompletedContent(influencerId, campaignType ? `${campaignType}=true` : "");

  return useInfiniteQuery<
    AxiosResponse<
      TPaginated<TInfluencerContent> & {
        ugc_application_video?: UGCMediaCompletedSerializer;
      }
    >,
    AxiosError,
    TQueryResultWithCount<TInfluencerContent[]>
  >({
    queryKey,
    initialPageParam: queryKey[0],
    getNextPageParam: lastPage => lastPage.data.next,
    enabled: !!influencerId,
    select: response => {
      const allResults = response.pages.flatMap(page => page.data.results);
      const ugcApplicationVideo = response.pages[0].data.ugc_application_video;

      if (ugcApplicationVideo) {
        allResults.unshift({
          ugc_medias: [ugcApplicationVideo],
          isUgcApplicationContent: true,
          campaign: {
            campaign_type: "UGC",
            title: i18n.t("influencer_profile:ugc_application"),
            company_profile: {company_name: "Refluenced"},
            completed: ugcApplicationVideo.modified,
          },
        } as TInfluencerContent);
      }

      return {
        count: response.pages[0].data.count + (!!ugcApplicationVideo ? 1 : 0),
        results: allResults,
      };
    },
  });
};

export const useInfluencerPaymentHistoryQuery = (influencerId: number) =>
  useGenericInfiniteQuery<InfluencerPaymentHistorySerializer>({
    queryKey: INFLUENCER_KEYS.qInfluencerPaymentHistory(influencerId),
    enabled: !!influencerId,
  });

export const useUgcVideoVerificationMutation = () =>
  useMutation({
    meta: {skipSuccessToast: true},
    mutationKey: INFLUENCER_KEYS.mUgcVideoVerification,
    mutationFn: ({
      rejectedReason,
      status,
      ugcVideoVerificationId,
    }: {
      rejectedReason?: string;
      status: "accepted" | "rejected";
      ugcVideoVerificationId: number;
    }) =>
      client.post(INFLUENCER_KEYS.mUgcVideoVerification[0], {
        rejected_reason: rejectedReason,
        status,
        ugc_video_verification: ugcVideoVerificationId,
      }),
  });

export const useUnsubscribeAllNotificationsMutation = ({
  influencerId,
  mutationOptions,
}: {
  influencerId: number;
  mutationOptions?: Partial<UseMutationOptions>;
}) =>
  useMutation({
    mutationKey: INFLUENCER_KEYS.mUnsubscribeAllNotifications(influencerId),
    mutationFn: () => client.post(INFLUENCER_KEYS.mUnsubscribeAllNotifications(influencerId)[0]),
    ...mutationOptions,
  });

export const useUpdateInfluencerFilterMutation = ({filterId, onSuccess}: {filterId?: number; onSuccess?: () => void}) =>
  useMutation({
    mutationKey: filterId ? INFLUENCER_KEYS.mUpdateInfluencerFilter(filterId) : [],
    mutationFn: (data: {filter_params?: TFilters; name?: string}) =>
      client.patch(INFLUENCER_KEYS.mUpdateInfluencerFilter(filterId!)[0], {
        ...data,
        ...(data.filter_params && {filter_params: omit(data.filter_params, FILTER_UNSAVEABLE_KEYS)}),
      }),
    onSuccess: () => {
      const filterBasePath = INFLUENCER_KEYS.qInfluencerFilters({})[0].split("?")[0];
      queryClient.invalidateQueries({
        predicate: query => typeof query.queryKey[0] === "string" && query.queryKey[0].startsWith(filterBasePath),
      });
      onSuccess?.();
    },
  });
